/**
 * Hook for managing workspace theme colors
 *
 * This hook:
 * - Reads accent color from workspace.theme_config (falls back to default indigo)
 * - Generates derived colors (light, dark, muted) from the primary color
 * - Injects CSS variables dynamically on :root
 *
 * Architecture:
 * - Sidebar: Always uses "night" mode (dark anthracite background)
 * - Content: Uses user's theme preference (day/night/auto)
 *
 * Color definitions: @/styles/colors.ts
 */

import { useEffect, useMemo } from "react";
import { useWorkspace } from "@/hooks/use-workspace";
import { useUserThemeMode, EffectiveThemeMode } from "@/hooks/use-user-theme-mode";
import { ThemeConfig, ThemeColorPoint } from "@/models/account";
import {
  hexToHsl,
  hslToHex,
  hexToRgbString,
  hexToRgb as hexToRgbObjectFromColor,
} from "@/utils/color";
import {
  DEFAULT_ACCENT,
  daySurfaces,
  nightSurfaces,
  sidebarColors,
} from "@/styles/colors";

// ============================================================================
// CSS Variable Application
// ============================================================================

/**
 * Apply a CSS variable update immediately
 * Direct application ensures reliable updates when switching workspaces
 */
function applyCSSUpdate(property: string, value: string) {
  document.documentElement.style.setProperty(property, value);
}

// Sidebar always uses dark mode for consistent design
const SIDEBAR_MODE: EffectiveThemeMode = "night";

// Default theme colors configuration (mode is now handled by useUserThemeMode)
export const DEFAULT_THEME_COLORS: ThemeColorPoint[] = [
  { id: "1", color: DEFAULT_ACCENT, x: 70, y: 30 }
];

/** Default theme configuration */
export const DEFAULT_THEME_CONFIG: ThemeConfig = {
  colors: DEFAULT_THEME_COLORS,
};

// Local alias for internal use (returns "r, g, b" string for rgba())
const hexToRgb = hexToRgbString;

// Re-export color utilities for convenience
export { hexToRgbString as hexToRgb };
export { hexToRgbObjectFromColor as hexToRgbObject };

/**
 * Adjust color lightness for day/night mode
 */
function adjustColorForMode(hex: string, mode: EffectiveThemeMode): string {
  const { h, s, l } = hexToHsl(hex);
  
  if (mode === "day") {
    // In day mode, slightly darken the accent for better contrast on light backgrounds
    return hslToHex(h, Math.min(s + 5, 100), Math.max(l - 8, 30));
  } else {
    // In night mode, slightly lighten for better visibility on dark backgrounds
    return hslToHex(h, s, Math.min(l + 5, 75));
  }
}

// ============================================================================
// Derived Colors Generation
// ============================================================================

export interface DerivedColors {
  // Primary accent colors
  accent: string;
  accentLight: string;
  accentDark: string;
  accentMuted: string;
  accentSubtle: string;
  // RGB values for dynamic opacity
  accentRgb: string;
  accentLightRgb: string;
  accentDarkRgb: string;
  // Shadow colors
  accentShadow: string;
  accentShadowLg: string;
  // Gradient colors (for multi-point themes)
  gradientColors: string[];
  gradientStart: string;
  gradientMiddle: string | null;
  gradientEnd: string;
  // Chart palette (harmonious colors derived from accent)
  chartPalette: string[];
  // Sidebar-specific colors (themed gradient backgrounds)
  sidebarBackground: string; // Solid color or CSS linear-gradient
  sidebarTextPrimary: string; // Pure white or black based on background
  sidebarTextSecondary: string; // White/black with opacity
  sidebarGradientStart: string;
  sidebarGradientMiddle: string;
  sidebarGradientEnd: string;
  sidebarAccentGlow: string;
  sidebarBorderAccent: string;
}

/**
 * Generate a harmonious chart palette from the accent color
 */
function generateChartPalette(baseHex: string, additionalColors: string[] = []): string[] {
  const { h, s, l } = hexToHsl(baseHex);
  
  // If we have additional colors from the theme, use them first
  const palette: string[] = [baseHex, ...additionalColors];
  
  // Generate additional harmonious colors to fill the palette
  const targetSize = 8;
  const hueShifts = [30, -30, 60, -60, 120, -120, 180];
  
  let i = 0;
  while (palette.length < targetSize && i < hueShifts.length) {
    const newHue = (h + hueShifts[i] + 360) % 360;
    const newColor = hslToHex(newHue, Math.max(s - 10, 40), l);
    if (!palette.includes(newColor)) {
      palette.push(newColor);
    }
    i++;
  }
  
  return palette;
}

/**
 * Generate sidebar-specific colors from the accent color and additional colors
 * Uses centralized color definitions from @/styles/colors.ts
 */
function generateSidebarColors(
  baseHex: string,
  _additionalColors: string[] = [],
  mode: EffectiveThemeMode
): {
  sidebarBackground: string;
  sidebarTextPrimary: string;
  sidebarTextSecondary: string;
  sidebarGradientStart: string;
  sidebarGradientMiddle: string;
  sidebarGradientEnd: string;
  sidebarAccentGlow: string;
  sidebarBorderAccent: string;
} {
  // Use centralized sidebar color definitions
  const colors = mode === "night" ? sidebarColors.night : sidebarColors.day;
  const glowOpacity = mode === "night" ? 0.15 : 0.1;

  return {
    sidebarBackground: colors.background,
    sidebarTextPrimary: colors.textPrimary,
    sidebarTextSecondary: colors.textSecondary,
    sidebarGradientStart: colors.gradientStart,
    sidebarGradientMiddle: colors.gradientMiddle,
    sidebarGradientEnd: colors.gradientEnd,
    sidebarAccentGlow: `rgba(${hexToRgb(baseHex)}, ${glowOpacity})`,
    sidebarBorderAccent: colors.borderAccent,
  };
}

/**
 * Generate derived colors from a base hex color and optional additional colors
 */
export function generateDerivedColors(
  baseColor: string,
  additionalColors: string[] = [],
  mode: EffectiveThemeMode = "night"
): DerivedColors {
  // Adjust base color for the current mode
  const adjustedBase = adjustColorForMode(baseColor, mode);
  const { h, s, l } = hexToHsl(adjustedBase);

  const accent = adjustedBase;
  const accentLight = hslToHex(h, Math.min(s + 10, 100), Math.min(l + 12, 85));
  const accentDark = hslToHex(h, Math.min(s + 5, 100), Math.max(l - 12, 25));
  const accentMuted = hslToHex(h, Math.max(s - 40, 20), mode === "night" ? 85 : 90);
  const accentSubtle = hslToHex(h, Math.max(s - 50, 15), mode === "night" ? 92 : 95);

  // Prepare gradient colors
  const allColors = [accent, ...additionalColors.map(c => adjustColorForMode(c, mode))];
  const gradientStart = allColors[0];
  const gradientEnd = allColors[allColors.length - 1] || gradientStart;
  const gradientMiddle = allColors.length === 3 ? allColors[1] : null;

  // Generate sidebar-specific colors (pass additional colors for gradient support)
  const sidebarColors = generateSidebarColors(adjustedBase, additionalColors, mode);

  return {
    accent,
    accentLight,
    accentDark,
    accentMuted,
    accentSubtle,
    accentRgb: hexToRgb(accent),
    accentLightRgb: hexToRgb(accentLight),
    accentDarkRgb: hexToRgb(accentDark),
    accentShadow: `rgba(${hexToRgb(accent)}, 0.25)`,
    accentShadowLg: `rgba(${hexToRgb(accent)}, 0.35)`,
    gradientColors: allColors,
    gradientStart,
    gradientMiddle,
    gradientEnd,
    chartPalette: generateChartPalette(accent, additionalColors),
    ...sidebarColors,
  };
}

// ============================================================================
// Surface Colors for Day/Night Mode
// ============================================================================

export interface SurfaceColors {
  surface: string;
  surfaceSecondary: string;
  surfaceTertiary: string;
  surfaceElevated: string;
  surfaceTinted: string; // Surface with subtle accent tint
  surfaceTintedSecondary: string; // Secondary surface with subtle accent tint
  contentPrimary: string;
  contentSecondary: string;
  contentTertiary: string;
  contentMuted: string;
  contentInverse: string;
  border: string;
  borderStrong: string;
  borderSubtle: string;
}

export function getSurfaceColors(mode: EffectiveThemeMode): SurfaceColors {
  // Use centralized color definitions from @/styles/colors.ts
  const surfaces = mode === "night" ? nightSurfaces : daySurfaces;

  return {
    surface: surfaces.surface,
    surfaceSecondary: surfaces.surfaceSecondary,
    surfaceTertiary: surfaces.surfaceTertiary,
    surfaceElevated: surfaces.surfaceElevated,
    surfaceTinted: surfaces.surface, // Will be overridden with accent-tinted version
    surfaceTintedSecondary: surfaces.surfaceSecondary, // Will be overridden with accent-tinted version
    contentPrimary: surfaces.contentPrimary,
    contentSecondary: surfaces.contentSecondary,
    contentTertiary: surfaces.contentTertiary,
    contentMuted: surfaces.contentMuted,
    contentInverse: surfaces.contentInverse,
    border: surfaces.border,
    borderStrong: surfaces.borderStrong,
    borderSubtle: surfaces.borderSubtle,
  };
}

// ============================================================================
// Tinted Surface Colors Generation
// ============================================================================

/**
 * Generate surface colors with a subtle tint from the accent color
 * This gives each workspace a unique atmospheric feel
 */
export function generateTintedSurfaces(
  accentHex: string,
  mode: EffectiveThemeMode
): { surfaceTinted: string; surfaceTintedSecondary: string } {
  const { h, s } = hexToHsl(accentHex);
  
  if (mode === "night") {
    // Night mode: Very subtle tint on dark backgrounds
    // Base: #1e1e28 (h: 240, s: 15, l: 14) - darker base for better contrast
    const tintInfluence = 0.08; // Very subtle influence
    const baseLightness = 14;
    const baseSaturation = 15;
    
    // Blend the accent hue into the surface
    const blendedSaturation = baseSaturation + (s - baseSaturation) * tintInfluence;
    
    return {
      surfaceTinted: hslToHex(h, Math.min(blendedSaturation, 20), baseLightness),
      surfaceTintedSecondary: hslToHex(h, Math.min(blendedSaturation * 0.8, 16), baseLightness + 2),
    };
  }
  
  // Day mode: Very subtle tint on light backgrounds
  // Base: #FAFBFC (h: 210, s: 20, l: 98)
  const tintInfluence = 0.06; // Even more subtle for day mode
  const baseLightness = 97;
  
  // Create a very light tinted surface
  const tintedSaturation = Math.min(s * tintInfluence + 3, 15);
  
  return {
    surfaceTinted: hslToHex(h, tintedSaturation, baseLightness),
    surfaceTintedSecondary: hslToHex(h, tintedSaturation * 1.2, baseLightness - 1.5),
  };
}

// ============================================================================
// CSS Variable Application
// ============================================================================

/**
 * Apply theme colors to CSS variables on :root
 * Uses requestAnimationFrame batching for smooth transitions
 * Note: Theme mode classes are now handled by useUserThemeMode
 */
function applyThemeToCSS(
  colors: DerivedColors,
  surfaces: SurfaceColors,
) {
  // Note: Mode classes (theme-day, theme-night) are now managed by useUserThemeMode
  // Apply CSS variables directly for reliable updates when switching workspaces

  // ========== Accent Colors ==========
  applyCSSUpdate("--arc-accent", colors.accent);
  applyCSSUpdate("--arc-accent-light", colors.accentLight);
  applyCSSUpdate("--arc-accent-dark", colors.accentDark);
  applyCSSUpdate("--arc-accent-muted", colors.accentMuted);
  applyCSSUpdate("--arc-accent-subtle", colors.accentSubtle);

  // ========== RGB Values (for dynamic opacity) ==========
  applyCSSUpdate("--arc-accent-rgb", colors.accentRgb);
  applyCSSUpdate("--arc-accent-light-rgb", colors.accentLightRgb);
  applyCSSUpdate("--arc-accent-dark-rgb", colors.accentDarkRgb);

  // ========== Shadows ==========
  applyCSSUpdate("--arc-shadow-accent", `0 4px 14px ${colors.accentShadow}`);
  applyCSSUpdate("--arc-shadow-accent-lg", `0 8px 20px ${colors.accentShadowLg}`);
  applyCSSUpdate("--arc-shadow-accent-color", colors.accentShadow);

  // ========== Border Focus ==========
  applyCSSUpdate("--arc-border-focus", colors.accent);

  // ========== Gradient Colors ==========
  applyCSSUpdate("--arc-gradient-start", colors.gradientStart);
  applyCSSUpdate("--arc-gradient-end", colors.gradientEnd);
  if (colors.gradientMiddle) {
    applyCSSUpdate("--arc-gradient-middle", colors.gradientMiddle);
    applyCSSUpdate(
      "--arc-gradient",
      `linear-gradient(135deg, ${colors.gradientStart} 0%, ${colors.gradientMiddle} 50%, ${colors.gradientEnd} 100%)`
    );
  } else if (colors.gradientColors.length > 1) {
    applyCSSUpdate("--arc-gradient-middle", "");
    applyCSSUpdate(
      "--arc-gradient",
      `linear-gradient(135deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`
    );
  } else {
    applyCSSUpdate("--arc-gradient-middle", "");
    applyCSSUpdate("--arc-gradient", colors.accent);
  }

  // ========== Chart Palette ==========
  colors.chartPalette.forEach((color, index) => {
    applyCSSUpdate(`--arc-chart-${index + 1}`, color);
  });

  // ========== Sidebar Colors ==========
  applyCSSUpdate("--arc-sidebar-background", colors.sidebarBackground);
  applyCSSUpdate("--arc-sidebar-text-primary", colors.sidebarTextPrimary);
  applyCSSUpdate("--arc-sidebar-text-secondary", colors.sidebarTextSecondary);
  applyCSSUpdate("--arc-sidebar-gradient-start", colors.sidebarGradientStart);
  applyCSSUpdate("--arc-sidebar-gradient-middle", colors.sidebarGradientMiddle);
  applyCSSUpdate("--arc-sidebar-gradient-end", colors.sidebarGradientEnd);
  applyCSSUpdate("--arc-sidebar-accent-glow", colors.sidebarAccentGlow);
  applyCSSUpdate("--arc-sidebar-border-accent", colors.sidebarBorderAccent);

  // ========== Surface Colors (Day/Night Mode) ==========
  applyCSSUpdate("--arc-surface", surfaces.surface);
  applyCSSUpdate("--arc-surface-secondary", surfaces.surfaceSecondary);
  applyCSSUpdate("--arc-surface-tertiary", surfaces.surfaceTertiary);
  applyCSSUpdate("--arc-surface-elevated", surfaces.surfaceElevated);
  
  // ========== Tinted Surface Colors (Accent-influenced backgrounds) ==========
  applyCSSUpdate("--arc-surface-tinted", surfaces.surfaceTinted);
  applyCSSUpdate("--arc-surface-tinted-secondary", surfaces.surfaceTintedSecondary);

  // ========== Content Colors ==========
  applyCSSUpdate("--arc-content-primary", surfaces.contentPrimary);
  applyCSSUpdate("--arc-content-secondary", surfaces.contentSecondary);
  applyCSSUpdate("--arc-content-tertiary", surfaces.contentTertiary);
  applyCSSUpdate("--arc-content-muted", surfaces.contentMuted);
  applyCSSUpdate("--arc-content-inverse", surfaces.contentInverse);

  // ========== Border Colors ==========
  applyCSSUpdate("--arc-border", surfaces.border);
  applyCSSUpdate("--arc-border-strong", surfaces.borderStrong);
  applyCSSUpdate("--arc-border-subtle", surfaces.borderSubtle);

  // ========== Tailwind-compatible aliases ==========
  applyCSSUpdate("--color-accent", colors.accent);
  applyCSSUpdate("--color-accent-light", colors.accentLight);
  applyCSSUpdate("--color-accent-dark", colors.accentDark);
  applyCSSUpdate("--color-accent-muted", colors.accentMuted);
  applyCSSUpdate("--color-accent-subtle", colors.accentSubtle);
  applyCSSUpdate("--color-accent-rgb", colors.accentRgb);
}

// ============================================================================
// Main Hook
// ============================================================================

export interface UseWorkspaceThemeReturn {
  /** Full theme config from workspace (colors only, mode is user preference) */
  themeConfig: ThemeConfig;
  /** Current effective mode (day or night) - from user preference */
  mode: EffectiveThemeMode;
  /** User's mode preference (day/night/auto) */
  userModePreference: "day" | "night" | "auto";
  /** Function to change user's mode preference */
  setUserMode: (mode: "day" | "night" | "auto") => void;
  /** Toggle between day and night mode */
  toggleMode: () => void;
  /** Primary accent color from workspace */
  primaryColor: string;
  /** All accent colors from workspace */
  allColors: ThemeColorPoint[];
  /** Derived colors (accent variations, gradients, etc.) */
  derivedColors: DerivedColors;
  /** Surface colors based on current mode */
  surfaceColors: SurfaceColors;
  /** Current workspace ID */
  workspaceId: string | undefined;
  /** Whether workspace has multiple accent colors (gradient) */
  isGradient: boolean;
  /** Chart color palette derived from accent */
  chartPalette: string[];
  /** Whether current mode is night */
  isNight: boolean;
  /** Whether current mode is day */
  isDay: boolean;
}

/**
 * Hook to manage and apply workspace theme
 * 
 * Reads accent color from workspace.theme_config and applies it to CSS variables.
 * Falls back to default indigo (#6366F1) if no color is set.
 * 
 * - Sidebar: Always uses "night" mode (dark anthracite background)
 * - Content: Uses user's theme preference (day/night/auto)
 */
export function useWorkspaceTheme(): UseWorkspaceThemeReturn {
  const { currentWorkspace } = useWorkspace();

  // Get user's theme mode preference
  const {
    mode: userModePreference,
    effectiveMode,
    setMode: setUserMode,
    toggleMode,
    isNight,
    isDay,
  } = useUserThemeMode();

  // Get workspace ID for dependency tracking
  const workspaceId = currentWorkspace?.id;
  
  // Serialize theme config for stable comparison (handles undefined/null cases)
  const themeConfigKey = JSON.stringify(currentWorkspace?.theme_config ?? null);

  // Get theme config from workspace, fallback to defaults
  const themeConfig = useMemo(() => {
    if (currentWorkspace?.theme_config) {
      return currentWorkspace.theme_config;
    }
    return DEFAULT_THEME_CONFIG;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeConfigKey, workspaceId]);

  // Get colors from workspace theme_config or use defaults
  const allColors = useMemo(() => {
    return currentWorkspace?.theme_config?.colors?.length 
      ? currentWorkspace.theme_config.colors 
      : DEFAULT_THEME_COLORS;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeConfigKey, workspaceId]);

  // Get the primary color (first color in the array)
  const primaryColor = allColors[0]?.color || DEFAULT_ACCENT;

  // Get additional colors for gradients (if any)
  const additionalColors: string[] = useMemo(() => {
    return allColors.slice(1).map(c => c.color);
  }, [allColors]);

  // Check if workspace has gradient (multiple colors)
  const isGradient = allColors.length > 1;

  // Generate derived colors - use SIDEBAR_MODE for sidebar colors (always night/dark)
  const derivedColors = useMemo(() => {
    return generateDerivedColors(primaryColor, additionalColors, SIDEBAR_MODE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryColor, additionalColors, workspaceId]);

  // Get surface colors based on user's theme preference
  const surfaceColors = useMemo(() => {
    const baseSurfaces = getSurfaceColors(effectiveMode);
    const tintedSurfaces = generateTintedSurfaces(primaryColor, effectiveMode);
    return {
      ...baseSurfaces,
      ...tintedSurfaces,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryColor, effectiveMode, workspaceId]);

  // Apply theme to CSS variables when workspace or colors change
  useEffect(() => {
    if (typeof window !== "undefined") {
      applyThemeToCSS(derivedColors, surfaceColors);
    }
  }, [derivedColors, surfaceColors, workspaceId]);

  return {
    themeConfig,
    mode: effectiveMode,
    userModePreference,
    setUserMode,
    toggleMode,
    primaryColor,
    allColors,
    derivedColors,
    surfaceColors,
    workspaceId,
    isGradient,
    chartPalette: derivedColors.chartPalette,
    isNight,
    isDay,
  };
}

export default useWorkspaceTheme;
